import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { DataTable, type DataTableFacet } from "@/components/data-table";
import type { ExportRow } from "@/components/data-table/export-utils";
import type { DataTableFeatures } from "@/components/data-table/features";
import { RowActionsMenu } from "@/components/data-table/row-actions-menu";
import { useCursorPagination } from "@/hooks/use-cursor-pagination";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { ScoreDetailSidePanel } from "./ScoreDetailSidePanel";
import { ScoreBar, StatusPill } from "./score-viz";
import type { CampaignLocation } from "./types";

interface Location extends CampaignLocation {
  latitude: number | null;
  longitude: number | null;
}

interface LocationsTableProps {
  campaignId: string;
  refreshKey: number;
  profileId?: string | null;
}

const STATUS_FACETS: DataTableFacet[] = [
  {
    column: "geocodeStatus",
    title: "Status",
    options: [
      { label: "Pending", value: "pending" },
      { label: "Success", value: "success" },
      { label: "Failed", value: "failed" },
      { label: "Ambiguous", value: "ambiguous" },
    ],
  },
];

const SERVER_SORT_COLUMNS = new Set([
  "addressRaw",
  "city",
  "state",
  "propertyType",
  "yearBuilt",
  "buildingSf",
  "geocodeStatus",
  "floScore",
  "createdAt",
]);

const PAGE_SIZE = 10;

function toExportRows(locations: Location[]): ExportRow[] {
  return locations.map((l) => ({
    Address: l.addressRaw,
    City: l.city || l.addressCanonical?.city || "",
    State: l.state || l.addressCanonical?.state || "",
    "Property Type": l.propertyType ?? "",
    "Year Built": l.yearBuilt ?? "",
    "Building SF": l.buildingSf ?? "",
    Units: l.unitCount ?? "",
    Status: l.geocodeStatus,
    Latitude: l.latitude ?? "",
    Longitude: l.longitude ?? "",
    FloScore: l.floScore ?? "",
    "Score Confidence": l.scoreConfidence ?? "",
    "Created At": l.createdAt,
  }));
}

export function LocationsTable({ campaignId, refreshKey, profileId }: LocationsTableProps) {
  const defaultSort: SortingState = useMemo(
    () => (profileId ? [{ id: "floScore", desc: true }] : []),
    [profileId],
  );

  const [sorting, setSorting] = useState<SortingState>(defaultSort);
  const [activeFacets, setActiveFacets] = useState<Record<string, string[]>>({});
  const [detailLocation, setDetailLocation] = useState<Location | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Reset sort default when the profile assignment changes (e.g. user picks a profile).
  useEffect(() => {
    setSorting(defaultSort);
  }, [defaultSort]);

  const filters = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    if (profileId) out.profileId = profileId;
    const statuses = activeFacets.geocodeStatus ?? [];
    if (statuses.length > 0) out.geocodeStatus = statuses.join(",");
    return out;
  }, [profileId, activeFacets]);

  const sortParam = useMemo(() => {
    const first = sorting[0];
    if (!first) return null;
    if (!SERVER_SORT_COLUMNS.has(first.id)) return null;
    return { column: first.id, direction: first.desc ? "desc" : "asc" } as const;
  }, [sorting]);

  const pagination = useCursorPagination<Location>({
    endpoint: `/api/campaigns/${campaignId}/locations`,
    limit: PAGE_SIZE,
    filters,
    sort: sortParam,
    refreshKey,
  });

  const handleActionRef = useRef((_action: string, _location: Location) => {});
  handleActionRef.current = (action: string, location: Location) => {
    if (action === "delete") {
      handleDeleteLocation(location);
    } else if (action === "viewScore") {
      setDetailLocation(location);
      setDetailOpen(true);
    }
  };

  async function handleDeleteLocation(location: Location) {
    try {
      const res = await fetch(`/api/campaigns/${campaignId}/locations/${location.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error ?? "Failed to delete location");
      }
      toast.success("Location deleted");
      // Force a refetch by resetting to page 1 (the deleted row may have been on this page).
      pagination.resetToFirstPage();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete location");
    }
  }

  const columns = useMemo<ColumnDef<DataTableFeatures, Location, unknown>[]>(() => {
    const cols: ColumnDef<DataTableFeatures, Location, unknown>[] = [
      {
        id: "addressRaw",
        accessorKey: "addressRaw",
        header: "Address",
        cell: ({ row }) => <span className="font-medium">{row.getValue("addressRaw")}</span>,
        enableSorting: true,
      },
      {
        id: "city",
        accessorFn: (row) => row.city || row.addressCanonical?.city || "",
        header: "City",
        cell: ({ row }) => row.getValue("city") || "—",
        enableSorting: true,
      },
      {
        id: "state",
        accessorFn: (row) => row.state || row.addressCanonical?.state || "",
        header: "State",
        cell: ({ row }) => row.getValue("state") || "—",
        enableSorting: true,
      },
      {
        id: "propertyType",
        accessorFn: (row) => row.propertyType ?? "",
        header: "Type",
        cell: ({ row }) => row.getValue("propertyType") || "—",
        enableSorting: true,
      },
      {
        id: "yearBuilt",
        accessorFn: (row) => row.yearBuilt ?? null,
        header: "Year Built",
        cell: ({ row }) => {
          const val = row.original.yearBuilt;
          return val != null ? <span className="tabular-nums">{val}</span> : "—";
        },
        enableSorting: true,
      },
      {
        id: "buildingSf",
        accessorFn: (row) => row.buildingSf ?? null,
        header: "Bldg SF",
        cell: ({ row }) => {
          const val = row.original.buildingSf;
          return val != null ? <span className="tabular-nums">{val.toLocaleString()}</span> : "—";
        },
        enableSorting: true,
      },
      {
        id: "scoreConfidence",
        accessorFn: (row) => row.scoreConfidence ?? "",
        header: "Confidence",
        cell: ({ row }) => {
          const c = row.original.scoreConfidence;
          return c ? (
            <ConfidenceBadge confidence={c} />
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
        enableSorting: false,
      },
      {
        id: "geocodeStatus",
        accessorFn: (row) => row.geocodeStatus,
        header: "Status",
        cell: ({ row }) => <StatusPill status={row.original.geocodeStatus} />,
        enableSorting: true,
      },
      {
        id: "floScore",
        accessorFn: (row) => row.floScore ?? null,
        header: "FloScore",
        cell: ({ row }) => <ScoreBar score={row.original.floScore} />,
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const actions =
            row.original.floScore != null
              ? [
                  {
                    label: "View Score",
                    onClick: () => handleActionRef.current("viewScore", row.original),
                  },
                ]
              : [];
          return (
            <RowActionsMenu
              label={`Actions for ${row.original.addressRaw}`}
              actions={actions}
              destructiveActions={[
                { label: "Delete", onClick: () => handleActionRef.current("delete", row.original) },
              ]}
            />
          );
        },
      },
    ];
    return cols;
  }, []);

  const handleFacetChange = useCallback((column: string, values: string[]) => {
    setActiveFacets((prev) => ({ ...prev, [column]: values }));
  }, []);

  const rowIdAccessor = useCallback((row: Location) => row.id, []);

  const exportRows = useMemo(() => toExportRows(pagination.data), [pagination.data]);

  // TanStack pagination state — kept in sync with the cursor-pagination "page"
  // so the table renders the right pageIndex even though we drive transitions
  // ourselves via the server-side pagination footer.
  const tanstackPagination: PaginationState = useMemo(
    () => ({ pageIndex: pagination.page - 1, pageSize: PAGE_SIZE }),
    [pagination.page],
  );

  const handleTanstackPaginationChange = useCallback(() => {
    /* no-op: pagination is driven by serverSidePagination buttons */
  }, []);

  return (
    <div className="h-full overflow-y-auto p-4">
      <DataTable
        title="Locations"
        subtitle="Addresses in this campaign"
        badge={pagination.total}
        columns={columns}
        data={pagination.data}
        rowIdAccessor={rowIdAccessor}
        onRowClick={(location) => {
          setDetailLocation(location);
          setDetailOpen(true);
        }}
        search={pagination.searchInput}
        onSearchChange={pagination.handleSearchInput}
        searchPlaceholder="Search locations..."
        facets={STATUS_FACETS}
        activeFacets={activeFacets}
        onFacetChange={handleFacetChange}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={tanstackPagination}
        onPaginationChange={handleTanstackPaginationChange}
        filteredRowCount={pagination.total}
        enablePagination
        enableSorting
        serverSide
        serverSidePagination={{
          total: pagination.total,
          page: pagination.page,
          pageSize: PAGE_SIZE,
          hasNext: pagination.hasNext,
          hasPrev: pagination.hasPrev,
          onNext: pagination.handleNext,
          onPrev: pagination.handlePrev,
          onFirst: pagination.resetToFirstPage,
        }}
        exportFileName="locations"
        exportRows={exportRows}
        isLoading={pagination.loading}
      />
      <ScoreDetailSidePanel
        open={detailOpen && !!detailLocation}
        onClose={() => setDetailOpen(false)}
        campaignId={campaignId}
        locationId={detailLocation?.id ?? ""}
        addressRaw={detailLocation?.addressRaw ?? ""}
        city={detailLocation?.city ?? detailLocation?.addressCanonical?.city ?? null}
        floScore={detailLocation?.floScore ?? null}
        confidence={detailLocation?.scoreConfidence ?? null}
      />
    </div>
  );
}
